import { lazy, useState } from 'react';
import { RouteObject } from 'react-router-dom';
import AuthGuard from '../components/AuthGuard';

const Home = lazy(() => import('../pages/home/page'));
const Dashboard = lazy(() => import('../pages/dashboard/page'));
const Translate = lazy(() => import('../pages/translate/page'));
const Docs = lazy(() => import('../pages/docs/page'));
const Admin = lazy(() => import('../pages/admin/page'));
const Login = lazy(() => import('../pages/login/page'));
const Register = lazy(() => import('../pages/register/page'));
const VerifyEmail = lazy(() => import('../pages/verify-email/page'));
const ForgotPassword = lazy(() => import('../pages/forgot-password/page'));
const ResetPassword = lazy(() => import('../pages/reset-password/page'));
const Blog = lazy(() => import('../pages/blog/page'));
const ArticlePage = lazy(() => import('../pages/blog/ArticlePage'));
const Guides = lazy(() => import('../pages/guides/page'));
const CaseStudies = lazy(() => import('../pages/case-studies/page'));
const CaseStudyPage = lazy(() => import('../pages/case-studies/CaseStudyPage'));
const NotFound = lazy(() => import('../pages/NotFound'));
const PaymentSuccess = lazy(() => import('../pages/payment/Success'));
const PaymentFailed = lazy(() => import('../pages/payment/Failed'));
const SimulatePayment = lazy(() => import('../pages/payment/Simulate'));
const SatimConfirmation = lazy(() => import('../pages/payment/SatimConfirmation'));

// Simple pages for legal links
const Privacy = () => (
  <div className="min-h-screen bg-white">
    {/* Header */}
    <div className="bg-gradient-to-r from-indigo-900 to-purple-900 text-white py-16">
      <div className="max-w-4xl mx-auto px-8">
        <h1 className="text-5xl font-bold mb-4">Privacy Policy</h1>
        <p className="text-xl text-indigo-200">Last updated: March 15, 2025</p>
      </div>
    </div>

    <div className="max-w-4xl mx-auto px-8 py-12">
      <div className="prose prose-lg max-w-none">
        <p className="text-gray-700 text-lg mb-8">
          This Privacy Policy describes how TranslateAPI ("we", "us", or "our") collects, uses, discloses, and protects your personal information when you use our translation services, API, and related products (collectively, the "Services").
        </p>

        <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-8">
          <p className="text-blue-800 font-semibold mb-2">Key Points:</p>
          <ul className="text-blue-700 list-disc pl-5 space-y-1">
            <li>We only collect information necessary to provide our translation services</li>
            <li>Text submitted for translation is processed but not stored long-term</li>
            <li>We use industry-standard security measures to protect your data</li>
            <li>We do not sell your personal information to third parties</li>
            <li>You have rights regarding your personal data under applicable laws</li>
          </ul>
        </div>

        <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">1. Information We Collect</h2>
        
        <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">1.1 Information You Provide</h3>
        <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
          <li><strong>Account Information:</strong> When you create an account, we collect your name, email address, and password.</li>
          <li><strong>Payment Information:</strong> If you purchase a paid plan, we collect billing details through our secure payment processor.</li>
          <li><strong>API Usage:</strong> When you use our API, we collect API keys, request logs, and usage statistics.</li>
          <li><strong>Communications:</strong> When you contact support, we collect your contact information and the content of your messages.</li>
        </ul>

        <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">1.2 Information We Collect Automatically</h3>
        <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
          <li><strong>Usage Data:</strong> We collect information about how you use our Services, including API request frequency, translation volumes, and feature usage.</li>
          <li><strong>Device Information:</strong> We collect information about the device you use to access our Services, including IP address, browser type, and operating system.</li>
          <li><strong>Cookies and Tracking:</strong> We use cookies and similar technologies to maintain your session and improve our Services.</li>
        </ul>

        <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">1.3 Content You Translate</h3>
        <p className="text-gray-700 mb-4">
          When you submit text for translation:
        </p>
        <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
          <li>Text is processed in real-time to provide translations</li>
          <li>Content may be temporarily cached for performance optimization</li>
          <li>We do not store translated content long-term unless required for legal compliance</li>
          <li>We do not use your content to train our AI models without explicit consent</li>
        </ul>

        <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">2. How We Use Your Information</h2>
        <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
          <li><strong>Provide Services:</strong> To deliver translation services, process payments, and maintain your account.</li>
          <li><strong>Improve Services:</strong> To analyze usage patterns and enhance our translation quality and performance.</li>
          <li><strong>Communicate:</strong> To send service updates, security alerts, and support responses.</li>
          <li><strong>Security:</strong> To detect and prevent fraud, abuse, and security incidents.</li>
          <li><strong>Compliance:</strong> To comply with legal obligations and enforce our terms.</li>
        </ul>

        <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">3. Data Sharing and Disclosure</h2>
        <p className="text-gray-700 mb-4">
          We may share your information in the following circumstances:
        </p>
        <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
          <li><strong>Service Providers:</strong> With trusted third parties who help us operate our Services (e.g., payment processors, hosting providers).</li>
          <li><strong>Legal Requirements:</strong> When required by law, court order, or government request.</li>
          <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets.</li>
          <li><strong>With Your Consent:</strong> When you explicitly authorize us to share your information.</li>
        </ul>
        <p className="text-gray-700 mb-6">
          <strong>Important:</strong> We do not sell your personal information to third parties for marketing purposes.
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">4. Data Security</h2>
        <p className="text-gray-700 mb-4">
          We implement appropriate technical and organizational measures to protect your personal information, including:
        </p>
        <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
          <li>Encryption of data in transit using TLS/SSL</li>
          <li>Secure storage of sensitive information</li>
          <li>Regular security assessments and audits</li>
          <li>Access controls and authentication mechanisms</li>
          <li>Incident response procedures</li>
        </ul>

        <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">5. Data Retention</h2>
        <p className="text-gray-700 mb-4">
          We retain your personal information only as long as necessary for the purposes outlined in this policy:
        </p>
        <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
          <li><strong>Account Information:</strong> Retained while your account is active and for a reasonable period thereafter</li>
          <li><strong>API Logs:</strong> Retained for 30 days for debugging and security purposes</li>
          <li><strong>Payment Information:</strong> Retained as required by financial regulations</li>
          <li><strong>Legal Compliance:</strong> Retained as required by applicable laws</li>
        </ul>

        <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">6. Your Rights</h2>
        <p className="text-gray-700 mb-4">
          Depending on your location, you may have the following rights regarding your personal information:
        </p>
        <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
          <li><strong>Access:</strong> Request a copy of your personal information</li>
          <li><strong>Correction:</strong> Request correction of inaccurate information</li>
          <li><strong>Deletion:</strong> Request deletion of your personal information</li>
          <li><strong>Restriction:</strong> Request restriction of processing</li>
          <li><strong>Portability:</strong> Request transfer of your data to another service</li>
          <li><strong>Objection:</strong> Object to certain types of processing</li>
        </ul>
        <p className="text-gray-700 mb-6">
          To exercise these rights, please contact us at translate@deeptranslate.online.
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">7. International Data Transfers</h2>
        <p className="text-gray-700 mb-6">
          Our Services are operated globally. Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place for such transfers in accordance with applicable data protection laws.
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">8. Children's Privacy</h2>
        <p className="text-gray-700 mb-6">
          Our Services are not intended for children under 16. We do not knowingly collect personal information from children under 16. If we become aware that we have collected personal information from a child under 16, we will take steps to delete such information.
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">9. Changes to This Policy</h2>
        <p className="text-gray-700 mb-6">
          We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on our website and updating the "Last updated" date. Your continued use of our Services after such changes constitutes your acceptance of the updated policy.
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">10. Contact Us</h2>
        <p className="text-gray-700 mb-4">
          If you have any questions or concerns about this Privacy Policy or our data practices, please contact us:
        </p>
        <div className="bg-gray-50 p-6 rounded-lg mb-8">
          <p className="text-gray-700 mb-2"><strong>Email:</strong> translate@deeptranslate.online</p>
          <p className="text-gray-700 mb-2"><strong>Address:</strong> Cité 40 Logs Ras El Oued, Bordj Bou Arreridj 34200, Algeria</p>
          <p className="text-gray-700"><strong>Data Protection Officer:</strong> translate@deeptranslate.online</p>
        </div>

        <div className="border-t border-gray-200 pt-8 mt-12">
          <p className="text-gray-600 text-sm">
            This Privacy Policy is based on industry standards and best practices for API services, similar to those used by Google Cloud Translation API, Amazon Translate, and Microsoft Translator.
          </p>
        </div>
      </div>
    </div>
  </div>
);

const Terms = () => (
  <div className="min-h-screen bg-white">
    {/* Header */}
    <div className="bg-gradient-to-r from-indigo-900 to-purple-900 text-white py-16">
      <div className="max-w-4xl mx-auto px-8">
        <h1 className="text-5xl font-bold mb-4">Terms of Service</h1>
        <p className="text-xl text-indigo-200">Last updated: March 15, 2025</p>
      </div>
    </div>

    <div className="max-w-4xl mx-auto px-8 py-12">
      <div className="prose prose-lg max-w-none">
        <p className="text-gray-700 text-lg mb-8">
          These Terms of Service ("Terms") govern your access to and use of TranslateAPI's translation services, API, and related products (collectively, the "Services"). By accessing or using our Services, you agree to be bound by these Terms.
        </p>

        <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 mb-8">
          <p className="text-yellow-800 font-semibold mb-2">Important Notice:</p>
          <p className="text-yellow-700">
            These Terms include important provisions, including limitations of liability, dispute resolution, and your responsibilities when using our API. Please read them carefully.
          </p>
        </div>

        <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">1. Acceptance of Terms</h2>
        <p className="text-gray-700 mb-6">
          By creating an account, accessing our API, or using any of our Services, you agree to these Terms. If you are using our Services on behalf of an organization, you represent that you have the authority to bind that organization to these Terms.
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">2. Account Registration</h2>
        <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
          <li>You must provide accurate and complete information when creating an account</li>
          <li>You are responsible for maintaining the confidentiality of your account credentials</li>
          <li>You are responsible for all activities that occur under your account</li>
          <li>You must notify us immediately of any unauthorized use of your account</li>
          <li>We reserve the right to refuse service, suspend, or terminate accounts at our discretion</li>
        </ul>

        <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">3. API Usage and Restrictions</h2>
        
        <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">3.1 API Access</h3>
        <p className="text-gray-700 mb-4">
          We grant you a non-exclusive, non-transferable, revocable license to access and use our API in accordance with these Terms and your subscription plan.
        </p>

        <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">3.2 Rate Limits</h3>
        <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
          <li>API usage is subject to rate limits based on your subscription plan</li>
          <li>Rate limits are measured in requests per second, minute, hour, or day</li>
          <li>Exceeding rate limits may result in throttling or temporary suspension</li>
          <li>You must not attempt to circumvent rate limits through any means</li>
        </ul>

        <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">3.3 Prohibited Uses</h3>
        <p className="text-gray-700 mb-4">You agree not to use our Services to:</p>
        <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
          <li>Violate any applicable laws, regulations, or third-party rights</li>
          <li>Generate or distribute spam, malware, or malicious content</li>
          <li>Attempt to reverse engineer, decompile, or disassemble our Services</li>
          <li>Use our Services to create a competing product or service</li>
          <li>Interfere with or disrupt the integrity or performance of our Services</li>
          <li>Attempt to gain unauthorized access to our systems or other users' accounts</li>
          <li>Use automated means to access our Services without our prior written consent</li>
          <li>Process content that is illegal, harmful, threatening, abusive, or infringing</li>
        </ul>

        <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">4. Content and Data</h2>
        
        <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">4.1 Your Content</h3>
        <p className="text-gray-700 mb-4">
          You retain all rights to the content you submit for translation. By submitting content, you grant us the right to process it solely for the purpose of providing translation services.
        </p>

        <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">4.2 Data Processing</h3>
        <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
          <li>We process content in real-time to provide translations</li>
          <li>Content may be temporarily cached for performance optimization</li>
          <li>We do not store translated content long-term unless required for legal compliance</li>
          <li>We do not use your content to train our AI models without explicit consent</li>
        </ul>

        <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">4.3 Content Restrictions</h3>
        <p className="text-gray-700 mb-6">
          You are solely responsible for the content you submit. You represent and warrant that you have all necessary rights to submit such content and that it does not violate any laws or third-party rights.
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">5. Pricing and Payment</h2>
        <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
          <li>Pricing is based on your selected subscription plan</li>
          <li>Payments are due in advance for subscription plans</li>
          <li>We may change prices with 30 days' notice</li>
          <li>All fees are non-refundable except as required by law</li>
          <li>You are responsible for all taxes associated with your use of our Services</li>
        </ul>

        <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">6. Intellectual Property</h2>
        <p className="text-gray-700 mb-6">
          Our Services, including our API, software, documentation, and all related intellectual property rights, are owned by TranslateAPI. These Terms do not grant you any rights to our trademarks, service marks, logos, or other brand features.
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">7. Disclaimer of Warranties</h2>
        <p className="text-gray-700 mb-6">
          OUR SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT OUR SERVICES WILL BE UNINTERRUPTED, ERROR-FREE, OR COMPLETELY SECURE. TRANSLATIONS ARE PROVIDED FOR INFORMATIONAL PURPOSES AND MAY NOT BE 100% ACCURATE.
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">8. Limitation of Liability</h2>
        <p className="text-gray-700 mb-6">
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, TRANSLATEAPI SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM YOUR USE OF OUR SERVICES.
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">9. Termination</h2>
        <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
          <li>You may terminate your account at any time</li>
          <li>We may suspend or terminate your access to our Services for violation of these Terms</li>
          <li>Upon termination, your right to use our Services immediately ceases</li>
          <li>Provisions that by their nature should survive termination will survive</li>
        </ul>

        <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">10. Changes to Terms</h2>
        <p className="text-gray-700 mb-6">
          We may modify these Terms at any time. We will provide notice of material changes by posting the updated Terms on our website and updating the "Last updated" date. Your continued use of our Services after such changes constitutes your acceptance of the updated Terms.
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">11. Governing Law and Dispute Resolution</h2>
        <p className="text-gray-700 mb-6">
          These Terms shall be governed by the laws of the State of California, without regard to its conflict of law provisions. Any disputes arising from these Terms shall be resolved through binding arbitration in San Francisco, California.
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">12. Contact Information</h2>
        <div className="bg-gray-50 p-6 rounded-lg mb-8">
          <p className="text-gray-700 mb-2"><strong>For legal notices:</strong> translate@deeptranslate.online</p>
          <p className="text-gray-700 mb-2"><strong>Address:</strong> Cité 40 Logs Ras El Oued, Bordj Bou Arreridj 34200, Algeria</p>
          <p className="text-gray-700"><strong>For general inquiries:</strong> translate@deeptranslate.online</p>
        </div>

        <div className="border-t border-gray-200 pt-8 mt-12">
          <p className="text-gray-600 text-sm">
            These Terms of Service are based on industry standards for API services, similar to those used by Google Cloud Translation API, Amazon Translate, and Microsoft Translator.
          </p>
        </div>
      </div>
    </div>
  </div>
);

const Cookies = () => (
  <div className="min-h-screen bg-white">
    {/* Header */}
    <div className="bg-gradient-to-r from-indigo-900 to-purple-900 text-white py-16">
      <div className="max-w-4xl mx-auto px-8">
        <h1 className="text-5xl font-bold mb-4">Cookie Policy</h1>
        <p className="text-xl text-indigo-200">Last updated: March 15, 2025</p>
      </div>
    </div>

    <div className="max-w-4xl mx-auto px-8 py-12">
      <div className="prose prose-lg max-w-none">
        <p className="text-gray-700 text-lg mb-8">
          This Cookie Policy explains how TranslateAPI ("we", "us", or "our") uses cookies and similar tracking technologies when you visit our website or use our Services. This policy should be read together with our Privacy Policy.
        </p>

        <div className="bg-green-50 border-l-4 border-green-500 p-6 mb-8">
          <p className="text-green-800 font-semibold mb-2">Cookie Consent:</p>
          <p className="text-green-700">
            By continuing to use our website, you consent to our use of cookies as described in this policy. You can manage your cookie preferences at any time through your browser settings.
          </p>
        </div>

        <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">1. What Are Cookies?</h2>
        <p className="text-gray-700 mb-6">
          Cookies are small text files that are placed on your computer, smartphone, or other device when you visit a website. They are widely used to make websites work more efficiently and to provide information to website owners.
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">2. Types of Cookies We Use</h2>
        
        <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">2.1 Essential Cookies</h3>
        <p className="text-gray-700 mb-4">
          These cookies are necessary for the website to function properly. They enable basic functions like page navigation and access to secure areas of the website.
        </p>
        <div className="bg-gray-50 p-4 rounded-lg mb-6">
          <p className="text-gray-700"><strong>Examples:</strong> Session cookies for authentication, security cookies to prevent fraud</p>
          <p className="text-gray-600 text-sm mt-2">These cookies cannot be disabled as they are essential for the website to work.</p>
        </div>

        <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">2.2 Functional Cookies</h3>
        <p className="text-gray-700 mb-4">
          These cookies enable the website to provide enhanced functionality and personalization. They may be set by us or by third-party providers whose services we have added to our pages.
        </p>
        <div className="bg-gray-50 p-4 rounded-lg mb-6">
          <p className="text-gray-700"><strong>Examples:</strong> Language preference cookies, theme preference cookies</p>
        </div>

        <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">2.3 Analytics Cookies</h3>
        <p className="text-gray-700 mb-4">
          These cookies allow us to count visits and traffic sources so we can measure and improve the performance of our site. They help us know which pages are the most and least popular and see how visitors move around the site.
        </p>
        <div className="bg-gray-50 p-4 rounded-lg mb-6">
          <p className="text-gray-700"><strong>Examples:</strong> Google Analytics cookies, performance monitoring cookies</p>
        </div>

        <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">2.4 Marketing Cookies</h3>
        <p className="text-gray-700 mb-6">
          These cookies may be set through our site by our advertising partners. They may be used by those companies to build a profile of your interests and show you relevant advertisements on other sites.
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">3. Specific Cookies We Use</h2>
        <div className="overflow-x-auto mb-8">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cookie Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Purpose</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">session_id</td>
                <td className="px-6 py-4 text-sm text-gray-500">Maintains user session for authentication</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Session</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Essential</td>
              </tr>
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">csrf_token</td>
                <td className="px-6 py-4 text-sm text-gray-500">Protects against cross-site request forgery attacks</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Session</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Essential</td>
              </tr>
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">language_pref</td>
                <td className="px-6 py-4 text-sm text-gray-500">Stores your preferred language for the interface</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">1 year</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Functional</td>
              </tr>
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">_ga</td>
                <td className="px-6 py-4 text-sm text-gray-500">Google Analytics - distinguishes users</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">2 years</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Analytics</td>
              </tr>
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">_gid</td>
                <td className="px-6 py-4 text-sm text-gray-500">Google Analytics - distinguishes users</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">24 hours</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Analytics</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">4. How to Manage Cookies</h2>
        
        <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">4.1 Browser Settings</h3>
        <p className="text-gray-700 mb-4">
          Most web browsers allow you to control cookies through their settings preferences. However, limiting the ability of websites to set cookies may worsen your overall user experience.
        </p>
        <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
          <li><strong>Chrome:</strong> Settings → Privacy and security → Cookies and other site data</li>
          <li><strong>Firefox:</strong> Options → Privacy & Security → Cookies and Site Data</li>
          <li><strong>Safari:</strong> Preferences → Privacy → Cookies and website data</li>
          <li><strong>Edge:</strong> Settings → Cookies and site permissions → Cookies and data stored</li>
        </ul>

        <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">4.2 Third-Party Tools</h3>
        <p className="text-gray-700 mb-6">
          You can also manage third-party advertising cookies by visiting websites such as <a href="https://www.youronlinechoices.com/" className="text-indigo-600 hover:text-indigo-800">Your Online Choices</a> or the <a href="https://optout.networkadvertising.org/" className="text-indigo-600 hover:text-indigo-800">Network Advertising Initiative</a>.
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">5. Do Not Track Signals</h2>
        <p className="text-gray-700 mb-6">
          Some browsers have a "Do Not Track" feature that lets you tell websites that you do not want to have your online activities tracked. Currently, we do not respond to browser "Do Not Track" signals as there is no standard for how websites should respond to such signals.
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">6. Changes to This Policy</h2>
        <p className="text-gray-700 mb-6">
          We may update this Cookie Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any material changes by posting the updated policy on our website and updating the "Last updated" date.
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">7. Contact Us</h2>
        <div className="bg-gray-50 p-6 rounded-lg mb-8">
          <p className="text-gray-700 mb-2">If you have any questions about our use of cookies, please contact us:</p>
          <p className="text-gray-700 mb-2"><strong>Email:</strong> translate@deeptranslate.online</p>
          <p className="text-gray-700"><strong>Address:</strong> Cité 40 Logs Ras El Oued, Bordj Bou Arreridj 34200, Algeria</p>
        </div>

        <div className="border-t border-gray-200 pt-8 mt-12">
          <p className="text-gray-600 text-sm">
            This Cookie Policy is based on industry standards and best practices for transparency about cookie usage, similar to policies used by major technology companies.
          </p>
        </div>
      </div>
    </div>
  </div>
);

// About Page
const About = () => (
  <div className="min-h-screen bg-white">
    {/* Header */}
    <div className="bg-gradient-to-r from-indigo-900 to-purple-900 text-white py-16">
      <div className="max-w-4xl mx-auto px-8">
        <h1 className="text-5xl font-bold mb-4">About Us</h1>
        <p className="text-xl text-indigo-200">Building the future of translation</p>
      </div>
    </div>

    <div className="max-w-4xl mx-auto px-8 py-12">
      <div className="prose prose-lg max-w-none">
        <p className="text-gray-700 text-lg mb-8">
          TranslateAPI is a leading provider of professional translation services powered by advanced artificial intelligence. Our mission is to break down language barriers and make global communication accessible to everyone.
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Our Mission</h2>
        <p className="text-gray-700 mb-6">
          We believe that language should never be a barrier to communication. Our team of engineers and linguists work together to create translation solutions that are accurate, fast, and affordable.
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Our Values</h2>
        <ul className="list-disc pl-6 text-gray-700 space-y-3 mb-6">
          <li><strong>Accuracy:</strong> We prioritize providing the most accurate translations possible</li>
          <li><strong>Speed:</strong> Our API delivers translations in milliseconds</li>
          <li><strong>Security:</strong> We protect your data with enterprise-grade security</li>
          <li><strong>Innovation:</strong> We continuously improve our translation technology</li>
        </ul>

        <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Our Team</h2>
        <p className="text-gray-700 mb-6">
          We are a diverse team of professionals passionate about language and technology. Our team includes machine learning engineers, NLP experts, professional translators, and customer support specialists.
        </p>

        <div className="bg-indigo-50 p-8 rounded-2xl mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Join Our Team</h3>
          <p className="text-gray-700 mb-6">
            We're always looking for talented individuals to join our team. Check out our Careers page for open positions.
          </p>
          <a href="/careers" className="inline-block px-6 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors">
            View Open Positions
          </a>
        </div>
      </div>
    </div>
  </div>
);

// Careers Page
const Careers = () => (
  <div className="min-h-screen bg-white">
    {/* Header */}
    <div className="bg-gradient-to-r from-indigo-900 to-purple-900 text-white py-16">
      <div className="max-w-4xl mx-auto px-8">
        <h1 className="text-5xl font-bold mb-4">Careers</h1>
        <p className="text-xl text-indigo-200">Join our team and help shape the future of translation</p>
      </div>
    </div>

    <div className="max-w-4xl mx-auto px-8 py-12">
      <div className="prose prose-lg max-w-none">
        <p className="text-gray-700 text-lg mb-8">
          At TranslateAPI, we're building the future of language technology. We offer competitive salaries, remote work opportunities, and the chance to work on challenging problems.
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Open Positions</h2>

        <div className="space-y-6">
          <div className="border border-gray-200 rounded-xl p-6 hover:border-indigo-300 transition-colors">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Senior Machine Learning Engineer</h3>
            <p className="text-gray-600 mb-4">Remote • Full-time</p>
            <p className="text-gray-700 mb-4">
              We're looking for an experienced ML engineer to help improve our translation models.
            </p>
            <a href="#" className="text-indigo-600 font-medium hover:text-indigo-700">Apply Now →</a>
          </div>

          <div className="border border-gray-200 rounded-xl p-6 hover:border-indigo-300 transition-colors">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Backend Developer</h3>
            <p className="text-gray-600 mb-4">Remote • Full-time</p>
            <p className="text-gray-700 mb-4">
              Join our backend team to build scalable API infrastructure.
            </p>
            <a href="#" className="text-indigo-600 font-medium hover:text-indigo-700">Apply Now →</a>
          </div>

          <div className="border border-gray-200 rounded-xl p-6 hover:border-indigo-300 transition-colors">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Customer Success Manager</h3>
            <p className="text-gray-600 mb-4">Remote • Full-time</p>
            <p className="text-gray-700 mb-4">
              Help our customers get the most out of our translation services.
            </p>
            <a href="#" className="text-indigo-600 font-medium hover:text-indigo-700">Apply Now →</a>
          </div>
        </div>

        <div className="bg-gray-50 p-8 rounded-2xl mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Don't see the right role?</h3>
          <p className="text-gray-700 mb-6">
            We're always looking for talented people. Send us your resume and we'll reach out when we have a position that matches your skills.
          </p>
          <a href="mailto:careers@translateapi.com" className="inline-block px-6 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors">
            Send us your resume
          </a>
        </div>
      </div>
    </div>
  </div>
);

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { contactApi } = await import('../services/api');
      await contactApi.submitContact(formData);
      setSuccess(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (err: any) {
      setError(err.message || 'Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
    {/* Header */}
    <div className="bg-gradient-to-r from-indigo-900 to-purple-900 text-white py-16">
      <div className="max-w-4xl mx-auto px-8">
        <h1 className="text-5xl font-bold mb-4">Contact Us</h1>
        <p className="text-xl text-indigo-200">Get in touch with our team</p>
      </div>
    </div>

    <div className="max-w-6xl mx-auto px-8 py-12">
      <div className="grid lg:grid-cols-3 gap-12">
        {/* Left Column - Contact Information */}
        <div className="lg:col-span-2">
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 text-lg mb-8">
              We're here to help you with any questions about our translation services, API integration, technical support, or enterprise solutions. Our team is committed to providing you with the best possible experience.
            </p>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-8 rounded-2xl border border-indigo-100">
                <div className="w-12 h-12 flex items-center justify-center bg-indigo-600 rounded-full mb-6">
                  <i className="ri-mail-line text-white text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">General Inquiries</h3>
                <p className="text-gray-600 mb-4">For general questions about our services, pricing, or features.</p>
                <p className="text-indigo-700 font-semibold">translate@deeptranslate.online</p>
                <p className="text-gray-500 text-sm mt-2">Response time: 24-48 hours</p>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-8 rounded-2xl border border-blue-100">
                <div className="w-12 h-12 flex items-center justify-center bg-blue-600 rounded-full mb-6">
                  <i className="ri-customer-service-2-line text-white text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Technical Support</h3>
                <p className="text-gray-600 mb-4">Need help with API integration, troubleshooting, or technical issues.</p>
                <p className="text-blue-700 font-semibold">translate@deeptranslate.online</p>
                <p className="text-gray-500 text-sm mt-2">Response time: 24 hours (business days)</p>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-8 rounded-2xl border border-green-100">
                <div className="w-12 h-12 flex items-center justify-center bg-green-600 rounded-full mb-6">
                  <i className="ri-briefcase-line text-white text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Enterprise Sales</h3>
                <p className="text-gray-600 mb-4">For custom enterprise solutions, volume pricing, and dedicated support.</p>
                <p className="text-green-700 font-semibold">translate@deeptranslate.online</p>
                <p className="text-gray-500 text-sm mt-2">Schedule a consultation call</p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-8 rounded-2xl border border-purple-100">
                <div className="w-12 h-12 flex items-center justify-center bg-purple-600 rounded-full mb-6">
                  <i className="ri-building-line text-white text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Office Address</h3>
                <p className="text-gray-600 mb-2">Cité 40 Logs Ras El Oued</p>
                <p className="text-gray-600 mb-2">Bordj Bou Arreridj 34200</p>
                <p className="text-gray-600">Algeria</p>
                <p className="text-gray-500 text-sm mt-4">Virtual meetings available worldwide</p>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Frequently Asked Questions</h2>
            
            <div className="space-y-6 mb-12">
              <div className="border border-gray-200 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">How do I get started with the API?</h3>
                <p className="text-gray-600">Create an account on our platform, generate an API key from your dashboard, and follow our comprehensive documentation to integrate our translation services into your application.</p>
              </div>

              <div className="border border-gray-200 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">What languages do you support?</h3>
                <p className="text-gray-600">We support 100+ languages including all major world languages. Check our documentation for the complete list of supported languages and language codes.</p>
              </div>

              <div className="border border-gray-200 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Do you offer custom pricing for high-volume usage?</h3>
                <p className="text-gray-600">Yes, we offer custom enterprise plans for high-volume translation needs. Contact our sales team to discuss your requirements and get a tailored quote.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Contact Form */}
        <div className="lg:col-span-1">
          <div className="bg-gradient-to-b from-gray-50 to-white rounded-2xl p-8 border border-gray-200 shadow-lg sticky top-8">
            {success ? (
              <div className="text-center py-8">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-check-line text-4xl text-green-600"></i>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Message Sent!</h2>
                <p className="text-gray-600 mb-6">Thank you for contacting us. We'll get back to you within 24 hours.</p>
                <button
                  onClick={() => {
                    setSuccess(false);
                    setFormData({ name: '', email: '', subject: '', message: '' });
                  }}
                  className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
            <>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Send us a message</h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2" htmlFor="name">
                  Your Name
                </label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2" htmlFor="email">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2" htmlFor="subject">
                  Subject
                </label>
                <select
                  id="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                >
                  <option value="">Select a topic</option>
                  <option value="general">General Inquiry</option>
                  <option value="technical">Technical Support</option>
                  <option value="sales">Enterprise Sales</option>
                  <option value="billing">Billing Question</option>
                  <option value="partnership">Partnership Opportunity</option>
                </select>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2" htmlFor="message">
                  Message
                </label>
                <textarea
                  id="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                  placeholder="Tell us how we can help you..."
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Sending...' : 'Send Message'}
              </button>
            </form>
            </>
            )}

            {!success && (
            <div className="mt-8 pt-8 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Connect with us</h3>
              <div className="flex space-x-4">
                <a href="#" className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-full hover:bg-gray-200 transition-colors">
                  <i className="ri-twitter-fill text-gray-600"></i>
                </a>
                <a href="#" className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-full hover:bg-gray-200 transition-colors">
                  <i className="ri-linkedin-fill text-gray-600"></i>
                </a>
                <a href="#" className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-full hover:bg-gray-200 transition-colors">
                  <i className="ri-github-fill text-gray-600"></i>
                </a>
                <a href="#" className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-full hover:bg-gray-200 transition-colors">
                  <i className="ri-discord-fill text-gray-600"></i>
                </a>
              </div>
            </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-16 pt-12 border-t border-gray-200">
        <div className="text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Business Hours</h3>
          <div className="grid md:grid-cols-3 gap-8 max-w-3xl mx-auto">
            <div className="text-center">
              <p className="text-gray-700 font-medium">Monday - Friday</p>
              <p className="text-gray-600">9:00 AM - 6:00 PM PST</p>
            </div>
            <div className="text-center">
              <p className="text-gray-700 font-medium">Technical Support</p>
              <p className="text-gray-600">24/7 for critical issues</p>
            </div>
            <div className="text-center">
              <p className="text-gray-700 font-medium">Emergency Contact</p>
              <p className="text-gray-600">+352 691 203 341</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);
};

const routes: RouteObject[] = [
  {
    path: '/',
    element: <Home />,
  },
  {
    path: '/login',
    element: <Login />,
  },
  {
    path: '/register',
    element: <Register />,
  },
  {
    path: '/verify-email',
    element: <VerifyEmail />,
  },
  {
    path: '/forgot-password',
    element: <ForgotPassword />,
  },
  {
    path: '/reset-password',
    element: <ResetPassword />,
  },
  {
    path: '/dashboard',
    element: (
      <AuthGuard preventAdmin>
        <Dashboard />
      </AuthGuard>
    ),
  },
  {
    path: '/translate',
    element: (
      <AuthGuard preventAdmin>
        <Translate />
      </AuthGuard>
    ),
  },
  {
    path: '/docs',
    element: <Docs />,
  },
  {
    path: '/blog',
    element: <Blog />,
  },
  {
    path: '/blog/:id',
    element: <ArticlePage />,
  },
  {
    path: '/guides',
    element: <Guides />,
  },
  {
    path: '/case-studies',
    element: <CaseStudies />,
  },
  {
    path: '/case-studies/:id',
    element: <CaseStudyPage />,
  },
  {
    path: '/admin',
    element: (
      <AuthGuard requireAdmin>
        <Admin />
      </AuthGuard>
    ),
  },
  {
    path: '/privacy',
    element: <Privacy />,
  },
  {
    path: '/terms',
    element: <Terms />,
  },
  {
    path: '/cookies',
    element: <Cookies />,
  },
  {
    path: '/contact',
    element: <Contact />,
  },
  {
    path: '/about',
    element: <About />,
  },
  {
    path: '/careers',
    element: <Careers />,
  },
  {
    path: '/payment/success',
    element: <PaymentSuccess />,
  },
  {
    path: '/payment/failed',
    element: <PaymentFailed />,
  },
  {
    path: '/payment/simulate',
    element: <SimulatePayment />,
  },
  {
    path: '/payment/satim-confirmation',
    element: <SatimConfirmation />,
  },
  {
    path: '*',
    element: <NotFound />,
  },
];

export default routes;
